/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionClosePopup';

const ActionClosePopupManager: ActionManagerFn<'ActionClosePopup'> = (
  platform: Platform,
): ActionManager<'ActionClosePopup'> => {
  return {
    ...baseActionManager(platform, 'ActionClosePopup'),
    defaultValue,
    props: {
      ...baseActionProps,
      targetItemId: {
        label: 'Target Item ID',
        type: 'ItemID',
        initialFilter: 'Popup',
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          if (item?.type !== 'Popup') {
            return `Item id '${value}' doesn't belong to a Popup`;
          }
          return '';
        },
      },
    },
    replaceableWith: ['ActionOpenPopup'],
    name: 'Close Popup',
    getDisplayText: (item) => item.props.targetItemId,
    icon: { iconName: 'times' },
    description: `**Overview**<br/>
The Close Popup action is a UI control component designed to programmatically dismiss a specific "Popup" modal within the application. It serves as the standard mechanism for closing modal dialogs based on user interactions or logic triggers, ensuring overlay content can be removed from the view.<br/><br/>
This component enforces strict type safety by validating that the targeted element is indeed a Popup component. It is designed to be interchangeable with the ActionOpenPopup component, allowing for streamlined workflow adjustments within the editor.

**Key Features**<br/>
* **Strict Type Validation:** Implements validateItemId to verify that the targetItemId corresponds to a component with type 'Popup', preventing runtime errors.
* **Component Interchangeability:** Explicitly configured in the replaceableWith array to allow direct swapping with ActionOpenPopup, facilitating quick logic reversal during development.
* **Registry Filtering:** Uses initialFilter: 'Popup' to restrict the selection interface, ensuring only relevant popup components are presented to the builder.

**Properties**<br/>
* **Target Item ID (targetItemId):** An ItemID property that identifies the specific popup to be closed.
  * *Validation:* Checks the platform registry for the item and confirms item.type === 'Popup'.
  * *Error Feedback:* Returns "Item id '[value]' doesn't belong to a Popup" if validation fails.

**Use Cases**<br/>
* **Workflow Completion:** Automatically closing a form modal after a "Submit" or "Save" action has successfully processed.
* **Manual Dismissal:** Assign to a "Cancel" or "Close" button within the popup to allow the user to exit the modal view.`,
  };
};

export default ActionClosePopupManager;
